import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState
} from "react";
import {
  useSessionContext,
  useAgent,
  useSessionMessages,
  useTrackToggle,
  RoomAudioRenderer,
  useEvents,
  SessionEvent
} from "@livekit/components-react";
import type { ReceivedMessage } from "@livekit/components-core";
import {
  Track,
  MediaDeviceFailure,
  ConnectionError,
  ConnectionErrorReason
} from "livekit-client";
import type { UseAgentReturn } from "@livekit/components-react";
import { Message } from "@/store/types";
import { Timestamp } from "firebase/firestore";
import { logger } from "@/utils";
import { crisisResources } from "@/constants";
import { useCrisisResources } from "@/hooks/crisis";

export type ConnectionState =
  "disconnected" | "connecting" | "connected" | "ready" | "reconnecting";

/**
 * Props for ChatRoom component
 */
interface ChatRoomProps {
  /** Callback when new message arrives from LiveKit
   *
   * Can return the same message id if the message content has been updated.
   */
  onMessage: (msg: Message) => void;
  /** Callback when connection state changes */
  onConnectionState: (state: ConnectionState) => void;
  /** Callback to provide send function to parent */
  onSendHandle: (send: (text: string) => void) => void;
  /** Callback to provide mic toggle function to parent */
  onMicToggleHandle: (toggle: () => void) => void;
  /** Callback when mic enabled state changes */
  onMicState: (enabled: boolean) => void;
  /** Callback to provide speaker toggle function to parent */
  onSpeakerToggleHandle: (toggle: () => void) => void;
  /** Callback when speaker enabled state changes */
  onSpeakerState: (enabled: boolean) => void;
  /** Callback when an error occurs */
  onError: (error: string | null) => void;
}

/**
 * Owns the LiveKit session for the chat experience.
 *
 * Mounts inside a SessionProvider (token must exist). On mount, starts the
 * session; on unmount, ends it. While mounted, forwards inbound messages,
 * connection state, and mic/speaker state to the parent via callback
 * props, and exposes send / mic-toggle / speaker-toggle handles back to
 * the parent. Surfaces unplanned agent failures via `onError` and handles
 * planned ends (crisis, duration timeout) by opening the crisis-resources
 * modal without showing an error.
 *
 * Renders only `RoomAudioRenderer` — every other interaction is
 * imperative via the callback props.
 */
export function ChatRoom({
  onMessage,
  onConnectionState,
  onSendHandle,
  onMicToggleHandle,
  onMicState,
  onSpeakerToggleHandle,
  onSpeakerState,
  onError
}: ChatRoomProps) {
  const session = useSessionContext();
  const agent = useAgent();
  const { messages: liveMessages, send } = useSessionMessages(session);
  const micTrack = useTrackToggle({ source: Track.Source.Microphone });
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const { openCooldown } = useCrisisResources();

  // ─── 1. Session lifecycle ──────────────────────────────────────────────

  // Session lifecycle - start on mount, end on unmount.
  // If session.start() throws (typically a LiveKit ConnectionError when the
  // signal-channel WebSocket can't be established — code 1006 / reason
  // ServerUnreachable on the user side), we surface the failure to the
  // parent via onError so the chat screen renders an ErrorDisplay rather
  // than sitting silently in the "connecting" state.
  useEffect(() => {
    async function start() {
      try {
        logger.info("Starting session");
        await session.start();
        logger.info("Session started");
      } catch (error: unknown) {
        logger.error("Failed to start session:", error);
        onError(messageForSessionStartError(error));
      }
    }
    start();
    return () => {
      logger.info("Ending session");
      session.end();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── 2. Media device errors ────────────────────────────────────────────

  // Listen to session events
  useEvents(
    session, // session has internal.emitter
    SessionEvent.MediaDevicesError,
    (error: Error) => {
      logger.error("Media device failure:", error);
      const failure = MediaDeviceFailure.getFailure(error);
      if (!failure) {
        return;
      }
      // Show user-friendly error message
      const errorMessage = "Media device failure: " + failure.toString();
      onError(errorMessage);
    }
  );

  // ─── 3. Inbound messages → parent ──────────────────────────────────────

  // Track processed messages to handle streaming (same ID, appending content)
  const processedMessagesRef = useRef<Map<string, string>>(new Map());

  // Forward new/updated messages to parent
  useEffect(() => {
    if (liveMessages.length === 0) return;

    // Process all messages - handle both new messages and streaming updates
    liveMessages.forEach((liveMessage) => {
      const messageId = liveMessage.id;
      const messageContent = liveMessage.message;

      // Check if this is a new message or an update to existing message
      const lastProcessedContent = processedMessagesRef.current.get(messageId);

      // Only process if content has changed (new message or streaming update)
      if (lastProcessedContent !== messageContent) {
        // Convert LiveKit message to our Message type
        const convertedMessage = convertLiveKitMessage(liveMessage);

        // Update our tracking
        processedMessagesRef.current.set(messageId, messageContent);

        // Forward to parent
        onMessage(convertedMessage);
      }
    });
  }, [liveMessages, onMessage]);

  // ─── 4. Connection state → parent ──────────────────────────────────────

  // Map and forward connection state
  const connectionState = useMemo(() => {
    const state = mapLiveKitState(agent);
    logger.info(
      `Connection state: ${state} [${session.connectionState}, ${agent.state}]`
    );
    return state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.connectionState, agent.state, agent.internal.agentParticipant]);

  useEffect(() => {
    onConnectionState(connectionState);
  }, [connectionState, onConnectionState]);

  // ─── 5. Planned-end RPC handshake + failure routing ────────────────────

  // Track planned ends signaled by the agent via RPC.
  //
  // The agent invokes a `planned_end` RPC (with a reason string) before
  // calling `shutdown(drain=True)` and awaits our ACK. The ACK is what
  // lets the agent proceed — so by the time the disconnect happens, we
  // have already opened any post-disconnect UX. Storing the reason in a
  // ref means the failureReasons effect can read it on the subsequent
  // ParticipantDisconnected event and suppress the toast.
  const plannedEndReasonRef = useRef<string | null>(null);

  // Stable refs for the callback props so the effect below depends only
  // on `session.room` — re-registering the RPC handler every time a
  // parent re-renders (which gives new function identities for
  // `openCooldown` / `onMessage`) would constantly tear down and
  // re-attach, with a brief window where an inbound RPC could land with
  // no handler. We mirror the latest values into refs and have the
  // handler read through them.
  const openCooldownRef = useRef(openCooldown);
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    openCooldownRef.current = openCooldown;
  }, [openCooldown]);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!session.room) return;
    const room = session.room;
    room.registerRpcMethod("planned_end", async ({ payload }) => {
      const reason = payload;
      logger.info(`Agent signaled planned end: ${reason}`);
      plannedEndReasonRef.current = reason;
      if (reason === "crisis") {
        // No resume time on this surface — the canonical timestamp comes
        // from the API's 423 response on the next session-start attempt.
        // The modal drops the "you can start at" line when resumeAt is
        // absent.
        openCooldownRef.current({
          reason: "crisis_cooldown",
          resources: crisisResources
        });
        onMessageRef.current({
          id: `system-crisis-${Date.now()}`,
          text: "Session ended. We've shared crisis resources with you.",
          role: "system",
          timestamp: Timestamp.now(),
          isTextInput: false
        });
      }
      return "ack";
    });
    return () => {
      room.unregisterRpcMethod("planned_end");
    };
  }, [session.room]);

  // Handle agent failure reasons.
  //
  // LiveKit's useAgent hook treats every agent-side ParticipantDisconnected
  // as a failure (`"Agent left the room unexpectedly."`). For planned ends
  // (crisis, future duration_hard_close, etc.) the agent has already told
  // us via the `planned_end` RPC; we set `plannedEndReasonRef` in that
  // handler and consult it here to suppress the error toast. Unplanned
  // disconnects (network errors, crashes) fall through to the error path.
  useEffect(() => {
    if (!agent.failureReasons || agent.failureReasons.length === 0) return;

    if (plannedEndReasonRef.current) {
      logger.info(
        `Suppressing failure surface; planned end (${plannedEndReasonRef.current}).`
      );
      session.end();
      return;
    }

    const errorMessage = "Agent failure: " + agent.failureReasons.join(", ");
    onError(errorMessage);
    // logger.error so Sentry sees this — the session is ending, not a soft warn.
    logger.error(
      `Agent failure. ending session: reasons=${agent.failureReasons} agentState=${agent.state} sessionState=${session.connectionState}`,
      new Error(errorMessage)
    );
    session.end();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.failureReasons, onError]);

  // ─── 6. Mic + speaker controls → parent ────────────────────────────────

  // Store the latest track toggle function in a ref to avoid infinite loops
  const micTrackRef = useRef(micTrack);
  useEffect(() => {
    micTrackRef.current = micTrack;
  }, [micTrack]);

  // Create stable toggle functions
  const stableMicToggle = useCallback(() => {
    logger.info("Toggling mic");
    micTrackRef.current.toggle();
  }, []);

  const stableSpeakerToggle = useCallback(() => {
    logger.info("Toggling speaker");
    setIsSpeakerEnabled((prev) => !prev);
  }, []);

  // Expose mic toggle to parent (only once or when onMicToggleHandle changes)
  useEffect(() => {
    logger.info("Exposing mic toggle to parent");
    onMicToggleHandle(stableMicToggle);
  }, [stableMicToggle, onMicToggleHandle]);

  // Expose speaker toggle to parent
  useEffect(() => {
    logger.info("Exposing speaker toggle to parent");
    onSpeakerToggleHandle(stableSpeakerToggle);
  }, [stableSpeakerToggle, onSpeakerToggleHandle]);

  // Forward mic state
  useEffect(() => {
    logger.info("Exposing mic state to parent");
    onMicState(micTrack.enabled);
  }, [micTrack.enabled, onMicState]);

  // Forward speaker state
  useEffect(() => {
    logger.info("Exposing speaker state to parent");
    onSpeakerState(isSpeakerEnabled);
  }, [isSpeakerEnabled, onSpeakerState]);

  // ─── 7. Send function handle → parent ──────────────────────────────────

  // Expose send function to parent
  useEffect(() => {
    logger.info("Exposing send to parent");
    onSendHandle(send);
  }, [send, onSendHandle]);

  // ONLY renders components that MUST be inside SessionProvider
  return <RoomAudioRenderer room={session.room} muted={!isSpeakerEnabled} />;
}

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Converts LiveKit message to our Message type
 */
function convertLiveKitMessage(liveMessage: ReceivedMessage): Message {
  return {
    id: liveMessage.id,
    text: liveMessage.message,
    role: mapRole(liveMessage.type!),
    timestamp: Timestamp.fromMillis(liveMessage.timestamp),
    isTextInput: liveMessage.type === "chatMessage"
  };
}

/**
 * Maps LiveKit agent states to our ConnectionState type
 */
function mapLiveKitState(agent: UseAgentReturn): ConnectionState {
  switch (agent.state) {
    case "connecting":
    case "pre-connect-buffering":
    case "idle":
      return "connecting";
    case "listening":
    case "thinking":
    case "speaking":
      return "ready";
    case "failed":
    case "disconnected":
      return "disconnected";
    default:
      throw new Error("Unknown agent state: " + agent.state);
  }
}

/**
 * Maps LiveKit message type to our Message roles
 */
function mapRole(messageType: string): "user" | "agent" | "system" {
  if (messageType === "userTranscript") return "user";
  if (messageType === "agentTranscript") return "agent";
  if (messageType === "chatMessage") return "user"; // Chat messages from user
  return "system";
}

/**
 * Translate a session.start() failure into a user-facing message.
 *
 * LiveKit's ConnectionError carries a `reason` enum we can branch on.
 * ServerUnreachable (the case we've actually observed in the wild — Sentry
 * shows HEAD + /rtc/v1/validate succeeding over HTTPS, only the WebSocket
 * upgrade fails) is almost always a middlebox / extension on the user's
 * network mangling the upgrade, not our server. Steer the copy accordingly.
 */
function messageForSessionStartError(error: unknown): string {
  if (error instanceof ConnectionError) {
    switch (error.reason) {
      case ConnectionErrorReason.ServerUnreachable:
      case ConnectionErrorReason.WebSocket:
        return "Couldn't reach the voice server. Check your network (firewall, VPN, or browser extensions can block voice chat) and try again.";
      case ConnectionErrorReason.Timeout:
        return "Voice chat timed out connecting. Please try again.";
      case ConnectionErrorReason.NotAllowed:
        return "Voice chat was refused. Please reload and try again.";
      default:
        return "Couldn't start voice chat. Please try again.";
    }
  }
  return error instanceof Error
    ? error.message
    : "Couldn't start voice chat. Please try again.";
}
